import { Culture, cultureLabels } from "../context/VolunteersHighlightContext/constants";
import {
	UpdateVolunteersFAQLocalizable,
	UpdateVolunteersFAQRequest,
	VolunteersFAQFormData,
	VolunteersFAQRules,
} from "../context/VolunteersFAQContext/constants";
import { getRichTextPlainText } from "../utils/richText";

const getCultureLabel = (culture: string) => cultureLabels[culture as Culture] ?? culture;

// undefined: not set (add) or unchanged (update); null: a filled value was cleared on the update form.
const validateDisplayOrder = (displayOrder?: number | null) => {
	if (displayOrder === undefined) {
		return null;
	}
	if (displayOrder === null) {
		return "Sıra numarası boş bırakılamaz.";
	}
	return Number.isInteger(displayOrder) && displayOrder >= 1 ? null : "Sıra numarası 1 veya daha büyük bir tam sayı olmalıdır.";
};

const validateQuestion = (label: string, question: string) => {
	const trimmed = question.trim();
	return trimmed.length > VolunteersFAQRules.questionMaxLength || !VolunteersFAQRules.textPattern.test(trimmed)
		? `${label} soru en fazla ${VolunteersFAQRules.questionMaxLength} karakter olmalı ve geçersiz karakter içermemelidir.`
		: null;
};

const validateAnswer = (label: string, answer: string) =>
	answer.trim().length > VolunteersFAQRules.answerMaxLength
		? `${label} cevap, biçimlendirmesiyle birlikte en fazla ${VolunteersFAQRules.answerMaxLength} karakter olmalıdır.`
		: null;

// An answer counts as empty when the editor holds only tags or spaces.
const isAnswerEmpty = (answer: string) => !getRichTextPlainText(answer);

// A culture that is created from scratch (add form, or a culture missing on the server) needs both fields.
const validateCompleteLocalizable = (culture: string, question = "", answer = "") => {
	const label = getCultureLabel(culture);

	if (!question.trim()) {
		return `Lütfen ${label} soru alanını doldurun.`;
	}
	if (isAnswerEmpty(answer)) {
		return `Lütfen ${label} cevap alanını doldurun.`;
	}

	return validateQuestion(label, question) ?? validateAnswer(label, answer);
};

// An existing culture is patched partially: only the fields that changed are present.
const validateChangedLocalizable = ({ culture, question, answer }: UpdateVolunteersFAQLocalizable) => {
	const label = getCultureLabel(culture);

	if (question !== undefined) {
		const questionError = question.trim() ? validateQuestion(label, question) : `${label} soru boş bırakılamaz.`;
		if (questionError) {
			return questionError;
		}
	}
	if (answer !== undefined) {
		return isAnswerEmpty(answer) ? `${label} cevap boş bırakılamaz.` : validateAnswer(label, answer);
	}

	return null;
};

export class VolunteersFAQValidator {
	static readonly validateAddVolunteersFAQ = (data: VolunteersFAQFormData): string | null => {
		const displayOrderError = validateDisplayOrder(data.displayOrder);
		if (displayOrderError) {
			return displayOrderError;
		}

		for (const localizable of data.volunteersFAQLocalizable) {
			const error = validateCompleteLocalizable(localizable.culture, localizable.question, localizable.answer);
			if (error) {
				return error;
			}
		}

		return null;
	};

	// The request only carries changed fields, so an empty value here means the user cleared a filled field.
	static readonly validateUpdateVolunteersFAQ = (data: UpdateVolunteersFAQRequest): string | null => {
		const displayOrderError = validateDisplayOrder(data.displayOrder);
		if (displayOrderError) {
			return displayOrderError;
		}

		for (const localizable of data.volunteersFAQLocalizable ?? []) {
			const error = localizable.volunteersFAQLocalizableId
				? validateChangedLocalizable(localizable)
				: validateCompleteLocalizable(localizable.culture, localizable.question, localizable.answer);
			if (error) {
				return error;
			}
		}

		return null;
	};
}
